import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import {
  assetIdSchema,
  type CustomerId,
  type FacilityId,
  workOrderIdSchema,
  workRequestIdSchema,
} from "../lib/workIds";
import type { WorkRequestStatus } from "../types/workRequest";

const WORK_REQUEST_PAGE_SIZE = "25";

export const workRequestSchema = z
  .object({
    id: workRequestIdSchema,
    requestNumber: z.string().min(1),
    title: z.string().min(1),
    description: z.string().nullable(),
    // Keep the optional requester-facing note alongside the structured reason
    // for both the administrator list and requester detail views.
    rejectionReason: z.string().nullable().optional(),
    declineReason: z
      .object({
        code: z.enum([
          "DUPLICATE",
          "ALREADY_SCHEDULED",
          "NOT_MAINTENANCE",
          "INSUFFICIENT_DETAIL",
          "OTHER",
          "CANCELED_BY_REQUESTER",
        ]),
        name: z.string().min(1),
      })
      .nullable()
      .default(null),
    status: z.object({
      code: z.enum(["PENDING", "APPROVED", "CANCELED", "DELETED", "DECLINED"]),
      name: z.string().min(1),
    }),
    priority: z
      .object({
        // Must track the seeded Work.Priority codes: an unlisted one fails the
        // parse for the whole response, emptying the queue.
        code: z.enum(["NONE", "HIGH", "MEDIUM", "LOW"]),
        name: z.string().min(1),
      })
      .nullable(),
    asset: z
      .object({
        id: z.number().int().positive(),
        publicId: assetIdSchema,
        name: z.string().min(1),
        assetCode: z.string().nullable(),
        locationName: z.string().nullable(),
      })
      .nullable(),
    requester: z.object({
      cognitoId: z.string().nullable().default(null),
      name: z.string().min(1),
    }),
    reviewer: z.object({ name: z.string().min(1) }).nullable(),
    lastUpdatedBy: z.object({ name: z.string().min(1) }).nullable(),
    submittedAt: z.string().datetime(),
    reviewedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    // Tolerates an older backend response during a rolling deployment — an
    // absent field must not fail zod's array parse and empty the whole queue.
    isUnread: z.boolean().default(false),
    // An older backend omits this; fall back to whether a Work Order summary
    // came back so a visible link still hides Approve during a rolling deploy.
    hasLinkedWorkOrder: z.boolean().optional(),
    workOrder: z
      .object({
        id: workOrderIdSchema,
        number: z.string().min(1),
        title: z.string().min(1),
        status: z.object({ code: z.string().min(1), name: z.string().min(1) }),
        updatedAt: z.string().datetime(),
      })
      .nullable()
      .default(null),
  })
  .transform((request) => ({
    ...request,
    hasLinkedWorkOrder:
      request.hasLinkedWorkOrder ?? request.workOrder !== null,
  }));

const workRequestMetadataSchema = z.object({
  counts: z.object({
    mine: z.number().int().nonnegative(),
    all: z.number().int().nonnegative(),
    awaiting: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative().default(0),
    done: z.number().int().nonnegative().default(0),
    rejected: z.number().int().nonnegative().default(0),
    high: z.number().int().nonnegative(),
  }),
  assets: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string().min(1),
        assetCode: z.string().nullable(),
      }),
    )
    .default([]),
  requesters: z
    .array(z.object({ cognitoId: z.string().min(1), name: z.string().min(1) }))
    .default([]),
  statuses: z.array(
    z.object({
      code: z.enum(["PENDING", "APPROVED", "CANCELED", "DELETED", "DECLINED"]),
      name: z.string().min(1),
    }),
  ),
});

const workRequestsResponseSchema = z
  .object({
    requests: z.array(workRequestSchema),
    nextCursor: z.string().nullable().default(null),
    metadata: workRequestMetadataSchema.optional(),
  })
  .transform((payload) => {
    if (payload.metadata) {
      return { ...payload, metadata: payload.metadata, serverFiltered: true };
    }

    const requests = payload.requests.filter(
      (request) => request.status.code !== "DELETED",
    );
    return {
      ...payload,
      serverFiltered: false,
      metadata: {
        counts: {
          mine: 0,
          all: requests.length,
          awaiting: requests.filter(
            (request) => request.status.code === "PENDING",
          ).length,
          approved: requests.filter(
            (request) => request.status.code === "APPROVED",
          ).length,
          done: requests.filter((request) => request.status.code === "CANCELED")
            .length,
          rejected: requests.filter(
            (request) => request.status.code === "DECLINED",
          ).length,
          high: requests.filter((request) => request.priority?.code === "HIGH")
            .length,
        },
        assets: Array.from(
          new Map(
            requests.flatMap((request) =>
              request.asset
                ? [
                    [
                      request.asset.publicId,
                      {
                        id: request.asset.publicId,
                        name: request.asset.name,
                        assetCode: request.asset.assetCode ?? null,
                      },
                    ],
                  ]
                : [],
            ),
          ).values(),
        ),
        requesters: Array.from(
          new Map(
            requests
              .filter((request) => request.requester.cognitoId)
              .map((request) => [
                request.requester.cognitoId as string,
                {
                  cognitoId: request.requester.cognitoId as string,
                  name: request.requester.name,
                },
              ]),
          ).values(),
        ).sort((left, right) => left.name.localeCompare(right.name)),
        statuses: Array.from(
          new Map(
            requests.map((request) => [
              request.status.code,
              { code: request.status.code, name: request.status.name },
            ]),
          ).values(),
        ).sort((left, right) => left.name.localeCompare(right.name)),
      },
    };
  });

export type WorkRequestFilters = {
  view: "all" | "mine" | "awaiting" | "high";
  search: string;
  priorities: string[];
  requestedBy: string[];
  statuses: string[];
  tabStatus?: WorkRequestStatus;
  submittedFrom?: string;
  submittedTo?: string;
  assetAssignment?: "assigned" | "unassigned";
  assetIds: string[];
};

export type WorkRequestsResponse = z.infer<typeof workRequestsResponseSchema>;

export function parseWorkRequestsResponse(
  payload: unknown,
): WorkRequestsResponse {
  return workRequestsResponseSchema.parse(payload);
}

async function fetchWorkRequests(
  customerId: CustomerId,
  facilityId: FacilityId,
  cursor: string | null,
  filters: WorkRequestFilters,
): Promise<WorkRequestsResponse> {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ].requests.$get({
    param: { customerId, facilityId },
    query: {
      limit: WORK_REQUEST_PAGE_SIZE,
      view: filters.view,
      ...(cursor ? { cursor } : {}),
      ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
      ...(filters.priorities.length
        ? { priorities: filters.priorities.join(",") }
        : {}),
      ...(filters.submittedFrom
        ? { submittedFrom: filters.submittedFrom }
        : {}),
      ...(filters.submittedTo ? { submittedTo: filters.submittedTo } : {}),
      ...(filters.requestedBy.length
        ? { requestedBy: filters.requestedBy.join(",") }
        : {}),
      ...(filters.statuses.length
        ? { statuses: filters.statuses.join(",") }
        : {}),
      ...(filters.tabStatus ? { tabStatus: filters.tabStatus } : {}),
      ...(filters.assetAssignment
        ? { assetAssignment: filters.assetAssignment }
        : {}),
      ...(filters.assetIds.length
        ? { assetIds: filters.assetIds.join(",") }
        : {}),
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch Work Requests: ${response.status}`);
  }
  return parseWorkRequestsResponse(await response.json());
}

export function useWorkRequestsQuery(
  filters: WorkRequestFilters,
  options?: { enabled?: boolean; pageCount?: number },
) {
  const { selectedCustomer, selectedFacility } = useWorkScope();

  const query = useInfiniteQuery({
    queryKey: [
      "work-requests",
      selectedCustomer?.id,
      selectedFacility?.id,
      selectedFacility?.role,
      filters,
    ],
    queryFn: ({ pageParam }) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      return fetchWorkRequests(
        selectedCustomer.id,
        selectedFacility.id,
        pageParam,
        filters,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled:
      Boolean(selectedCustomer && selectedFacility) &&
      (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    // Requests uses the separate new-count query to offer an explicit refresh
    // banner. A focus refetch here would silently replace the list first and
    // make that banner impossible to show after returning to the tab.
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    select: (data) => ({
      requests: data.pages
        .slice(0, options?.pageCount)
        .flatMap((page) => page.requests),
      metadata: data.pages[0]?.metadata,
      serverFiltered: data.pages[0]?.serverFiltered ?? false,
      pageCount: data.pages.length,
    }),
    meta: { errorMessage: "Failed to load Work Requests." },
  });
  return {
    ...query,
    hasNextPage:
      query.hasNextPage ||
      (options?.pageCount !== undefined &&
        (query.data?.pageCount ?? 0) > options.pageCount),
  };
}
